/**
 * Listing URL Utilities
 *
 * URL format: /listings/{slug}-{public_id}
 *
 * The slug-segment compose/extract + `PUBLIC_ID_LENGTH` are the GENERATED
 * `slug-url.gen` module (routes-SSOT ADR 0142) — the web mirror of the
 * `slug_url` Rust crate, the single cross-Trinity implementation the backend
 * and mobile also consume. This file only adds the React-Router route wrapping.
 */

import { composeSegment, extractPublicId, PUBLIC_ID_LENGTH } from '@/shared/lib/url/slug-url.gen';
import { routes, type Route } from '@letsgethome/routes';
import { slug as toSlug, type PublicId, type Slug } from '@letsgethome/types/branded';

// Re-export the SSOT primitives so existing import sites keep working.
export { PUBLIC_ID_LENGTH, extractPublicId };

/**
 * Build a listing URL from slug and public_id (`{slug}-{publicId}`, or just the
 * public id when the slug is empty).
 *
 * @example
 * buildListingUrl('taikoo-shing-tower-1', 'abc123def456') // '/listings/taikoo-shing-tower-1-abc123def456'
 * buildListingUrl('', 'abc123def456') // '/listings/abc123def456'
 */
export function buildListingUrl(slug: Slug | undefined, publicId: PublicId): Route {
  return routes.listings.detail(toSlug(composeSegment(slug ?? '', publicId)));
}

/**
 * Build a listing URL from a listing object that has slug and publicId.
 * Falls back to just publicId if slug is empty.
 */
export function getListingHref(listing: { slug?: Slug; publicId: PublicId }): Route {
  return buildListingUrl(listing.slug, listing.publicId);
}
